"use client";

import { FC, useActionState, useEffect, useRef } from "react";

import { sendForm } from "@Features/get-in-touch/actions";
import { FormStateModel } from "@Features/get-in-touch/models";
import { PAGES } from "@Shared/config";
import { ElProps } from "@Shared/types";
import { ButtonSubmit, Input, Link, Text } from "@Shared/ui";
import { twcx } from "@Shared/utils";

const initialState: FormStateModel = {
    success: false,
    error: null,
};

export type FormProps = ElProps<"form"> & {
    onSuccess?: () => void;
    onError?: () => void;
};

export const Form: FC<FormProps> = ({ className, onSuccess, onError, ...restProps }) => {
    const refForm = useRef<HTMLFormElement>(null);

    const [formState, formAction] = useActionState(sendForm, initialState);

    useEffect(() => {
        if (formState.error?.fields) {
            (
                refForm.current?.querySelector(`[name="${Object.keys(formState.error.fields)[0]}"]`) as HTMLElement
            )?.focus();
        }

        if (formState.error) {
            onError?.();
        }

        if (formState.success) {
            refForm.current?.reset();

            onSuccess?.();
        }
    }, [formState]);

    return (
        <form
            ref={refForm}
            className={twcx("flex flex-col gap-5", "md:gap-3", className)}
            action={formAction}
            {...restProps}
        >
            <div className={twcx("flex flex-col gap-3", "md:gap-4")}>
                <Input
                    id="git-name"
                    label="Name*"
                    type="text"
                    name="name"
                    defaultValue={formState.defaultValues?.name}
                    placeholder="Enter your full name"
                    required
                    error={formState.error?.fields?.name}
                />

                <Input
                    id="git-email"
                    label="Email*"
                    type="email"
                    name="email"
                    defaultValue={formState.defaultValues?.email}
                    placeholder="your@email.com"
                    required
                    error={formState.error?.fields?.email}
                />

                <Input
                    id="git-message"
                    label="Message*"
                    type="text"
                    name="message"
                    defaultValue={formState.defaultValues?.message}
                    placeholder="Enter your text"
                    required
                    isTextarea
                    error={formState.error?.fields?.message}
                />
            </div>

            <div className={twcx("flex flex-col gap-5")}>
                <Text className="!text-[12px] !leading-[130%]" as="p" color="text-light-gray">
                    By clicking the &quot;Send&quot; button, you agree to the processing of your personal data in
                    accordance with our{" "}
                    <Link
                        className="underline lg:transition-colors lg:hover:text-accent-1 lg:hover:no-underline"
                        href={PAGES.privacyPolicy}
                        target="_blank"
                    >
                        Privacy Policy
                    </Link>
                    .
                </Text>

                <ButtonSubmit className="lg:self-start">Send</ButtonSubmit>

                {formState.error?.message && (
                    <Text variant="body-small" color="error-2">
                        {formState.error.message}
                    </Text>
                )}
            </div>
        </form>
    );
};
